import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { collection, query, orderBy, onSnapshot, where } from 'firebase/firestore';
import { useFirebase } from '../contexts/FirebaseContext';
import { useFavorites } from '../contexts/FavoritesContext';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { Search, Heart, ChevronLeft, Utensils, UtensilsCrossed, Pizza, Flame, Fish, Coffee, ShoppingBag, ShoppingCart, Leaf, Wine, Palette, Sparkles, GlassWater, Monitor, Home, Star as StarIcon, MapPin, CircleX, AlertCircle } from 'lucide-react';
import CartSheet from '../components/ios/CartSheet';

// Mirrors iOS RestaurantTypeSelectorView: back-chevron nav,
// "Search restaurants..." field, restaurant grid cards with
// banner image + gradient overlay + name/location.
function RestaurantTypePage() {
  const { restaurantType } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const businessType = searchParams.get('businessType') === 'retail' ? 'retail' : 'food';
  const backToMenu = () => navigate(`/dashboard/menu?businessType=${businessType}`);
  const { db, firebaseError } = useFirebase();
  const { isFavorite, toggleFavorite } = useFavorites();
  const { getItemCount, items: cartItems } = useCart();
  const { currentUser } = useAuth();

  const [searchText, setSearchText] = useState('');
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCartSheet, setShowCartSheet] = useState(false);

  // Get restaurant type info — names match iOS restaurantType(for:),
  // colors match iOS colorForRestaurantType
  const getRestaurantTypeInfo = (type) => {
    const types = {
      'KFC': { icon: Flame, color: '#E63333', name: 'KFC', description: 'Fried chicken, buckets, sides' },
      'Fast Food': { icon: UtensilsCrossed, color: '#FF3B30', name: 'Fast Food', description: 'Burgers, fries, local dishes, oxtail, stew chicken, steamed fish' },
      'Bar & Grill': { icon: Wine, color: '#FF3B30', name: 'Bar & Grill', description: 'Grilled dishes, steaks, wings, drinks, casual dining' },
      'Pizza': { icon: Pizza, color: '#FFCC00', name: 'Pizza', description: 'Pizza, pasta, calzones, Italian cuisine' },
      'Bakery': { icon: Coffee, color: '#B36633', name: 'Bakery', description: 'Bread, pastries, cakes, cookies, desserts' },
      'Cafe': { icon: Coffee, color: '#AF52DE', name: 'Cafe', description: 'Coffee, eggs, omelets, pancakes, waffles, breakfast items' },
      'Juice Stand': { icon: GlassWater, color: '#66CC99', name: 'Juice Stand', description: 'Lemonade, coconut water, fresh squeezed cane juice, natural beverages' },
      'BBQ Grill': { icon: UtensilsCrossed, color: '#4D66CC', name: 'BBQ Grill', description: 'Barbecue chicken, grilled pork, ribs, pigtail, garlic beer, local drinks' },
      'Seafood Restaurant': { icon: Fish, color: '#00B3CC', name: 'Seafood Restaurant', description: 'Fish, shrimp, lobster, seafood platters, ocean cuisine' },
      'Asian': { icon: UtensilsCrossed, color: '#339966', name: 'Asian', description: 'Chinese, Japanese, Thai, noodles, rice dishes, stir-fry' },
      'Indian': { icon: UtensilsCrossed, color: '#E6801A', name: 'Indian', description: 'Halal, gyro, shawarma, naan, biryani, tandoori, Indian cuisine' },
      'Latin': { icon: UtensilsCrossed, color: '#CC664D', name: 'Latin', description: 'Tacos, burritos, Latin cuisine' },
      'Mexican': { icon: UtensilsCrossed, color: '#D94D4D', name: 'Mexican', description: 'Tacos, burritos, Mexican cuisine' },
      'Sushi': { icon: UtensilsCrossed, color: '#4D9980', name: 'Sushi', description: 'Sushi rolls, sashimi, bento boxes' },
      'Steak House': { icon: UtensilsCrossed, color: '#BF4040', name: 'Steak House', description: 'Premium steaks, grilled meats' },
      'Local Restaurant': { icon: UtensilsCrossed, color: '#5973B3', name: 'Local Restaurant', description: 'Local dishes, comfort food' },
      'Roti/Patty': { icon: UtensilsCrossed, color: '#D98C26', name: 'Roti/Patty', description: 'Locally made chicken roti and patties' },
      'Vegetarian': { icon: Leaf, color: '#66CC66', name: 'Vegetarian', description: 'Plant-based, vegan, salads, healthy options, meat-free' },
      'Snack Boxes': { icon: UtensilsCrossed, color: '#F29933', name: 'Snack Boxes', description: 'Combo meals, snack boxes, party foods, platters, catering, sandwiches, breakfast items' },
      'Fish & Meat': { icon: Fish, color: '#CC3333', name: 'Fish & Meat', description: 'Fresh fish from fishermen, locally butchered meat, organic, local produce' },
      'Retail Store': { icon: ShoppingBag, color: '#994DCC', name: 'Retail Store', description: 'General retail, everyday items, convenience goods' },
      'Electronics Store': { icon: Monitor, color: '#3380CC', name: 'Electronics Store', description: 'Phones, computers, accessories, gadgets, electronics' },
      'Home Goods / Hardware Store': { icon: Home, color: '#99664D', name: 'Home Goods', description: 'Furniture, appliances, tools, hardware, home decor, kitchenware, building supplies' },
      'Home Goods': { icon: Home, color: '#99664D', name: 'Home Goods', description: 'Furniture, appliances, tools' },
      'Beauty Supply': { icon: StarIcon, color: '#E66699', name: 'Beauty Supply', description: 'Hair care, wigs, nails, makeup, lashes, men\'s grooming, beauty products' },
      'Perfume Store': { icon: Sparkles, color: '#CC80B3', name: 'Perfume Store', description: 'Perfumes, colognes, fragrances, body sprays, scents' },
      'Jewelry Store': { icon: Sparkles, color: '#E6CC33', name: 'Jewelry Store', description: 'Rings, necklaces, bracelets, watches, fine jewelry' },
      'Pharmacy': { icon: UtensilsCrossed, color: '#B3334D', name: 'Pharmacy', description: 'Pharmacy, health, wellness' },
      'Farmer': { icon: Leaf, color: '#4DB34D', name: 'Farmer', description: 'Fresh produce, vegetables, fruits, herbs, local harvest' },
      'Supermarket': { icon: ShoppingCart, color: '#CC8033', name: 'Supermarket', description: 'From all your local supermarkets, groceries, household items, dairy, bakery, pantry staples' },
      'Craft & Souvenirs': { icon: Palette, color: '#664DB3', name: 'Craft & Souvenirs', description: 'Local crafts, souvenirs, handmade goods, gifts, artwork' },
      'Liquor Store & Smoke Shop': { icon: Wine, color: '#803399', name: 'Liquor/Smoke', description: 'Duty free liquor, wines, cigars, vapes, rolling papers, smoking accessories' },
      'Water Supply': { icon: GlassWater, color: '#3399E6', name: 'Water Supply', description: 'Bottled water, ice, water delivery, water coolers, dispensers' },
      'Car Parts': { icon: UtensilsCrossed, color: '#FF3B30', name: 'Car Parts', description: 'Auto parts, tires, accessories, car maintenance, tools' }
    };
    return types[type] || { icon: Utensils, color: '#8E8E93', name: type, description: '' };
  };

  const typeInfo = getRestaurantTypeInfo(restaurantType);
  const IconComponent = typeInfo.icon;

  useEffect(() => {
    if (!firebaseError && db) {
      loadRestaurants();
    } else {
      setLoading(false);
      setError('Failed to load items');
    }
  }, [firebaseError, db, restaurantType]);

  const loadRestaurants = () => {
    setLoading(true);
    setError('');

    try {
      const restaurantsRef = collection(db, 'restaurants');
      const q = query(
        restaurantsRef,
        where('restaurantType', '==', restaurantType),
        where('isActive', '==', true),
        orderBy('name', 'asc')
      );

      const unsubscribe = onSnapshot(q, (snapshot) => {
        const restaurantsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setRestaurants(restaurantsData);
        setLoading(false);
      }, (err) => {
        console.error('Error loading restaurants:', err);
        setError('Failed to load restaurants');
        setLoading(false);
      });

      return unsubscribe;
    } catch (err) {
      console.error('Error setting up query:', err);
      setError('Failed to load restaurants');
      setLoading(false);
    }
  };

  const handleToggleFavorite = (e, restaurant) => {
    e.stopPropagation();
    if (!currentUser) {
      navigate('/login');
      return;
    }
    toggleFavorite({
      id: restaurant.id,
      name: restaurant.name,
      description: restaurant.description,
      imageURL: restaurant.bannerURL,
      restaurantId: restaurant.id
    });
  };

  // iOS RestaurantTypeSelectorViewModel gate: island + isActive (query) +
  // isAcceptingOrders. Island comes from the same localStorage key the
  // IslandPicker writes — the page reloads when it changes.
  const firestoreIsland = (typeof window !== 'undefined' &&
    localStorage.getItem('chef_selected_island') === 'nevis') ? 'Nevis' : 'St. Kitts';
  const normalizeIsland = (value) =>
    String(value || '').toLowerCase().includes('nevis') ? 'Nevis' : 'St. Kitts';

  const filteredRestaurants = restaurants.filter(restaurant => {
    const matchesIsland = normalizeIsland(restaurant.island) === firestoreIsland;
    const isAccepting = restaurant.isAcceptingOrders !== false;
    const matchesSearch = !searchText ||
      restaurant.name.toLowerCase().includes(searchText.toLowerCase()) ||
      (restaurant.description && restaurant.description.toLowerCase().includes(searchText.toLowerCase()));
    return matchesIsland && isAccepting && matchesSearch;
  });

  if (loading) {
    return (
      <div className="bg-[#F2F2F7] min-h-full flex flex-col items-center justify-center gap-3 py-20">
        <div className="w-8 h-8 rounded-full border-[3px] border-gray-200 border-t-[#FF3B30] animate-spin" />
        <p className="text-[15px] text-[#8E8E93]">Loading restaurants...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-[#F2F2F7] min-h-full flex flex-col items-center justify-center gap-4 py-20 px-8">
        <AlertCircle size={48} className="text-[#FF3B30]" strokeWidth={1.5} />
        <h2 className="text-[20px] font-semibold text-gray-900">{error}</h2>
        <button onClick={loadRestaurants} className="text-[15px] font-semibold text-white bg-red-600 px-5 py-2.5 rounded-xl">
          Retry
        </button>
        <button onClick={backToMenu} className="text-[15px] font-medium text-[#FF3B30]">
          Back to Menu
        </button>
      </div>
    );
  }

  return (
    <div className="bg-[#F2F2F7] min-h-full pb-28">
      {/* iOS nav bar — back chevron + centered type name */}
      <div className="sticky top-0 z-30 bg-white shadow-[0_1px_2px_rgba(0,0,0,0.08)]">
        <div className="h-12 flex items-center px-1 relative">
          <button onClick={backToMenu} className="p-2" aria-label="Back">
            <ChevronLeft size={24} className="text-gray-800" />
          </button>
          <h1 className="absolute left-1/2 -translate-x-1/2 flex items-center gap-1.5 text-[17px] font-semibold text-gray-900 max-w-[60%]">
            <IconComponent size={18} style={{ color: typeInfo.color }} className="shrink-0" />
            <span className="truncate">{typeInfo.name}</span>
          </h1>
          <div className="flex-1" />
        </div>
      </div>

      {/* iOS search field — "Search restaurants..." */}
      <div className="px-[7px] pt-3">
        <div className="flex items-center gap-2 bg-[#E5E5EA] rounded-[10px] px-3 py-2">
          <Search size={17} className="text-[#8E8E93] shrink-0" />
          <input
            type="text"
            placeholder="Search restaurants..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            className="flex-1 min-w-0 bg-transparent text-[15px] text-gray-900 placeholder-[#8E8E93] focus:outline-none"
          />
          {searchText !== '' && (
            <button onClick={() => setSearchText('')} aria-label="Clear search">
              <CircleX size={17} className="text-[#8E8E93]" />
            </button>
          )}
        </div>
      </div>

      {/* Results count */}
      <p className="px-[15px] py-2 text-[13px] text-[#8E8E93]">
        {filteredRestaurants.length} {typeInfo.name.toLowerCase()} found
      </p>

      {/* Restaurant grid — iOS RestaurantCard: banner + gradient + name/location */}
      {filteredRestaurants.length > 0 ? (
        <div className="px-[7px] grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {filteredRestaurants.map(restaurant => (
            <div
              key={restaurant.id}
              onClick={() => navigate(`/restaurant/${restaurant.id}`)}
              className="relative h-[200px] rounded-xl overflow-hidden bg-white shadow-sm ring-1 ring-black/5 cursor-pointer active:scale-[0.98] transition-transform"
            >
              {/* Banner image — iOS RestaurantGridCard frame: a blurred cover
                  fills the letterbox while the sharp image scales to FIT
                  (no crop), same as the menu item cards */}
              {restaurant.bannerURL ? (
                <>
                  <img
                    src={restaurant.bannerURL}
                    alt=""
                    aria-hidden
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover blur-md scale-125 opacity-80"
                  />
                  <img
                    src={restaurant.bannerURL}
                    alt={restaurant.name}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-contain"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                </>
              ) : (
                <div className="absolute inset-0 bg-gradient-to-br from-gray-300 to-gray-400 flex items-center justify-center">
                  <IconComponent size={40} className="text-white/50" />
                </div>
              )}

              {/* Gradient overlay */}
              <div className="absolute inset-x-0 bottom-0 p-4 bg-gradient-to-t from-black/80 to-transparent">
                <h3 className="text-[18px] font-bold text-white leading-tight drop-shadow-[0_1px_2px_rgba(0,0,0,0.5)]">
                  {restaurant.name}
                </h3>
                {restaurant.description && (
                  <p className="text-[14px] text-white/90 truncate drop-shadow-[0_1px_2px_rgba(0,0,0,0.5)] mb-1.5">
                    {restaurant.description}
                  </p>
                )}
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-1">
                    <MapPin size={14} className="text-white" />
                    <span className="text-xs text-white/90 drop-shadow-[0_1px_2px_rgba(0,0,0,0.5)]">
                      {restaurant.location || restaurant.island}
                    </span>
                  </span>
                  <button
                    onClick={(e) => handleToggleFavorite(e, restaurant)}
                    className="w-7 h-7 bg-white/20 rounded-full flex items-center justify-center"
                    aria-label="Favorite"
                  >
                    <Heart
                      size={14}
                      className={`drop-shadow-[0_1px_2px_rgba(0,0,0,0.5)] ${isFavorite(restaurant.id) ? 'text-[#FF3B30]' : 'text-white'}`}
                      fill={isFavorite(restaurant.id) ? '#FF3B30' : 'none'}
                    />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        /* iOS empty state */
        <div className="px-8 py-16 text-center flex flex-col items-center gap-3">
          <IconComponent size={48} style={{ color: '#9ca3af' }} />
          <h2 className="text-[17px] font-bold text-gray-900">No Restaurants Found</h2>
          <p className="text-[15px] text-[#8E8E93]">
            {searchText ? 'Try adjusting your search terms' : 'Check back later for new restaurants'}
          </p>
          <button onClick={backToMenu} className="mt-4 text-[15px] font-semibold text-white bg-red-600 px-5 py-2.5 rounded-xl">
            Browse Other Categories
          </button>
        </div>
      )}

      {/* Floating cart FAB */}
      {cartItems.length > 0 && (
        <button
          onClick={() => setShowCartSheet(true)}
          className="fixed bottom-[100px] right-4 z-50 w-14 h-14 rounded-full bg-[#FF3B30] shadow-lg flex items-center justify-center active:scale-95 transition-transform"
          aria-label="Open cart"
        >
          <ShoppingCart size={24} className="text-white" />
          {getItemCount() > 0 && (
            <span className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-[#34C759] text-white text-xs font-bold flex items-center justify-center">
              {getItemCount() > 99 ? '99+' : getItemCount()}
            </span>
          )}
        </button>
      )}
      <CartSheet open={showCartSheet} onClose={() => setShowCartSheet(false)} />
    </div>
  );
}

export default RestaurantTypePage;
